import React, { type ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { FondNuit } from './FondNuit';
import { ScoreRing } from './ScoreRing';
import { t } from '../i18n';
import { colors, formes, spacing, typography } from '../theme/tokens';

export interface CarteScoreProps {
  score: number;
  // Le verdict en une phrase (« Correct, peut mieux faire ») : le texte est fourni par l'écran, qui
  // sait de quel outil il s'agit et dans quelle langue.
  titre: string;
  detail?: string;
  // Bouton ou lien posé sous le détail, dans la colonne de texte (« Voir le détail »).
  action?: ReactNode;
  children?: ReactNode;
  testID?: string;
  // Identifiants propres à l'écran, quand des tests existants les attendent sous un autre nom que
  // ceux dérivés de `testID`.
  anneauTestID?: string;
  detailTestID?: string;
  titreTestID?: string;
}

/**
 * Carte nuit qui résume un score : anneau à gauche, verdict et détail à droite, et un emplacement
 * dessous pour un bouton ou une liste de critères. Sert aux résultats de CV, de lettre, de
 * LinkedIn et au hub Mon CV.
 *
 * Elle ne calcule ni verdict ni couleur : la règle de l'anneau vit dans `ScoreRing`, les libellés
 * dans l'écran.
 */
export function CarteScore({ score, titre, detail, action, children, testID, anneauTestID, detailTestID, titreTestID }: CarteScoreProps) {
  return (
    <FondNuit testID={testID} style={styles.carte}>
      <View style={styles.resume}>
        <ScoreRing
          score={score}
          size={formes.anneau.md}
          variante="degrade"
          surFond="sombre"
          legende={t('common.scoreSur100')}
          testID={anneauTestID ?? (testID ? `${testID}-anneau` : undefined)}
        />
        <View style={styles.textes}>
          <Text testID={titreTestID} style={styles.titre}>
            {titre}
          </Text>
          {detail ? (
            <Text testID={detailTestID ?? (testID ? `${testID}-detail` : undefined)} style={styles.detail}>
              {detail}
            </Text>
          ) : null}
          {action ? <View style={styles.action}>{action}</View> : null}
        </View>
      </View>
      {children ? <View style={styles.dessous}>{children}</View> : null}
    </FondNuit>
  );
}

const styles = StyleSheet.create({
  carte: { borderRadius: formes.rayon.lg, padding: spacing.lg },
  resume: { flexDirection: 'row', alignItems: 'center', gap: spacing.lg },
  // `flex: 1` + `minWidth: 0` : le verdict passe à la ligne au lieu de pousser l'anneau.
  textes: { flex: 1, minWidth: 0, gap: spacing.xs },
  titre: {
    fontSize: typography.h3.size,
    lineHeight: typography.h3.lineHeight,
    fontWeight: '800',
    color: colors.white,
  },
  detail: {
    fontSize: typography.small.size,
    lineHeight: typography.small.lineHeight,
    color: colors.blancAtenue,
  },
  action: { alignSelf: 'flex-start', marginTop: spacing.xs },
  dessous: { marginTop: spacing.lg },
});
